import { formatDay, importErrorMessage } from 'pages/data-import/utils';
import type { Tone } from 'theme/tokens';
import { IssueSummary, IssueTabKey, Numeric, RenameEvent } from './types';

export const PAGE_SIZE = 20;

/** Gõ xong chừng này mili giây mới gửi từ khoá lên máy chủ. */
export const SEARCH_DEBOUNCE_MS = 400;

/** Nhãn rủi ro của mọi dòng ở tab "Vắng khỏi file" (đặc tả đợt 2, mục 3). */
export const RISK_LABEL = 'Có thể đã xoá tài khoản hoặc bị loại';

// Bảng trống phải nói rõ vì sao (đặc tả đợt 2, mục 4).
export const NO_FILE_TEXT = 'Chưa có file nào được nhập';
export const EMPTY_GROUP_TEXT = 'Không có Creator nào thuộc nhóm này';
export const LOADING_TEXT = 'Đang tải dữ liệu…';

/** Tab Đã rời đại lý: chưa tìm lại được UID thì chưa biết Creator có tài khoản app nào. */
export const UNKNOWN_ACCOUNT_TEXT = 'Chưa rõ (chưa có UID)';

export const noMatchText = (keyword: string) => `Không có Creator nào khớp "${keyword}"`;

type CountKey = 'departed' | 'missing' | 'renamed' | 'unmatched';

export interface IssueTabMeta {
  key: IssueTabKey;
  label: string;
  /** Trường số đếm trong GET /data-import/issues/summary. */
  countKey: CountKey;
  /** Phần đuôi của đường dẫn: `${API.dataIssues}/${endpoint}`. */
  endpoint: string;
  /** Một câu ngắn dưới con số ở ô tóm tắt. */
  hint: string;
  tone: Tone;
}

/** Bốn nhóm vấn đề, đúng thứ tự tab. Tab đầu là tab mặc định. */
export const ISSUE_TABS: IssueTabMeta[] = [
  {
    key: 'roi-di',
    label: 'Đã rời đại lý',
    countKey: 'departed',
    endpoint: 'departed',
    hint: 'File TikTok ghi là đã rời khỏi đại lý',
    tone: 'danger',
  },
  {
    key: 'vang-mat',
    label: 'Vắng khỏi file',
    countKey: 'missing',
    endpoint: 'missing',
    hint: 'Từng có trong file, nay không còn',
    tone: 'warning',
  },
  {
    key: 'doi-id',
    label: 'Đổi TikTok ID',
    countKey: 'renamed',
    endpoint: 'renames',
    hint: 'Đã đổi TikTok ID ít nhất một lần',
    tone: 'info',
  },
  {
    key: 'chua-co-tai-khoan',
    label: 'Chưa có tài khoản',
    countKey: 'unmatched',
    endpoint: 'unmatched',
    hint: 'Có số liệu nhưng chưa có tài khoản trong app',
    tone: 'purple',
  },
];

export const DEFAULT_TAB: IssueTabKey = ISSUE_TABS[0].key;

/** Tab đang mở theo `?tab=`; giá trị lạ → tab mặc định. */
export function activeTabOf(requested?: string | null): IssueTabKey {
  return ISSUE_TABS.find((tab) => tab.key === requested)?.key ?? DEFAULT_TAB;
}

// ------------------------------------------------------------------ số

const numberFormat = new Intl.NumberFormat('vi-VN');

/** Số đếm của máy chủ (số hoặc chuỗi) → số; không đọc được thì 0. */
export function toCount(value?: Numeric | null): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Số nguyên kiểu vi-VN ("1.234.567"). BIGINT về dạng chuỗi dài hơn 15 chữ số thì tách nhóm
 * bằng tay, vì đổi qua Number sẽ làm sai các chữ số cuối.
 */
export function formatCount(value?: Numeric | null): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return Number.isFinite(value) ? numberFormat.format(value) : '—';
  const text = value.trim();
  const match = /^(-?)(\d+)$/.exec(text);
  if (!match) {
    const parsed = Number(text);
    return text && Number.isFinite(parsed) ? numberFormat.format(parsed) : '—';
  }
  const digits = match[2].replace(/^0+(?=\d)/, '');
  if (digits.length <= 15) return numberFormat.format(Number(`${match[1]}${digits}`));
  return `${match[1]}${digits.replace(/\B(?=(\d{3})+$)/g, '.')}`;
}

// ------------------------------------------------------------------ TikTok ID

/** TikTok ID hiển thị có một dấu @ ở đầu; trống → "". */
export function atName(name?: string | null): string {
  const clean = (name || '').trim().replace(/^@+/, '');
  return clean ? `@${clean}` : '';
}

const dayKey = (value?: string | null) => (value || '').slice(0, 10);

/** Các lần đổi xếp từ cũ tới mới (theo ngày đầu thấy TikTok ID mới). */
export function sortEvents(events?: RenameEvent[] | null): RenameEvent[] {
  const list = Array.isArray(events) ? events.slice() : [];
  return list
    .map((event, index) => ({ event, index }))
    .sort(
      (a, b) =>
        dayKey(a.event.firstSeenNew).localeCompare(dayKey(b.event.firstSeenNew)) ||
        a.index - b.index
    )
    .map((entry) => entry.event);
}

/** Lần đổi gần nhất; chưa đổi lần nào → undefined. */
export function latestEvent(events?: RenameEvent[] | null): RenameEvent | undefined {
  const sorted = sortEvents(events);
  return sorted[sorted.length - 1];
}

/** "từ @a sang @b" */
export function renameChangeText(event: RenameEvent): string {
  return `từ ${atName(event.from) || '(trống)'} sang ${atName(event.to) || '(trống)'}`;
}

/**
 * Thời điểm đổi: biết đúng ngày (hai file một-ngày liền nhau) → "ngày DD/MM/YYYY" là ngày đầu
 * thấy TikTok ID mới; ngược lại → "trong khoảng DD/MM/YYYY → DD/MM/YYYY".
 */
export function renameWhenText(event: RenameEvent): string {
  if (event.precision === 'DAY') {
    return `ngày ${formatDay(event.firstSeenNew || event.lastSeenOld)}`;
  }
  return `trong khoảng ${formatDay(event.lastSeenOld)} → ${formatDay(event.firstSeenNew)}`;
}

// ------------------------------------------------------------------ API

const isoDay = (value: unknown): string | null =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;

/** Điền mặc định cho GET /data-import/issues/summary để giao diện không vỡ khi thiếu trường. */
export function normalizeSummary(data: any): IssueSummary {
  return {
    latestDay: isoDay(data?.latestDay),
    latestPeriodEnd: isoDay(data?.latestPeriodEnd),
    departed: toCount(data?.departed),
    missing: toCount(data?.missing),
    renamed: toCount(data?.renamed),
    unmatched: toCount(data?.unmatched),
  };
}

/** Lời báo lỗi của trang này; các câu còn lại dùng chung với trang Nhập dữ liệu. */
export function issuesErrorMessage(err: any): string {
  const status: number | undefined = err?.statusCode || err?.status;
  const raw = typeof err?.message === 'string' ? err.message : '';
  if (status === 404 && /^Cannot GET /i.test(raw)) {
    return 'Máy chủ chưa có chức năng Vấn đề dữ liệu. Hãy báo bộ phận kỹ thuật cập nhật máy chủ rồi tải lại trang.';
  }
  return importErrorMessage(err);
}
